import Link from "next/link";
import React from "react";
import SiteShell, { APP_URL } from "@/components/SiteShell";
import FoundingMemberCallout from "@/components/FoundingMemberCallout";

/**
 * Honest comparison: FrazzleCRM vs Make (ex-Integromat). Different
 * categories — say so plainly. Pricing facts checked 2026-07.
 */

const ROWS: [string, string, string][] = [
  ["What it is", "A CRM where the funnel map IS the automation", "A general-purpose integration platform (3,000+ apps)"],
  ["Billing unit", "Flat plans — internal automation runs are unlimited", "Credits/operations: every trigger, filter and step counts"],
  ["Cost at volume", "Same price at 10× the sends", "10k credits ≈ $9–21/mo; heavy funnels burn credits fast"],
  ["Email/SMS sequences", "First-class: compose surface, merge fields, waits, send windows, broken-link watchdog", "Possible, but you assemble it from modules yourself"],
  ["CRM & contacts", "Built in: contacts, tags, fields, appointments, pipelines", "Not a CRM — you wire one in"],
  ["Visual model", "One map = funnel + automation + live traffic view", "Scenario diagrams per workflow (genuinely good visual builder)"],
  ["AI / MCP", "Built-in MCP server for AI agents", "AI modules for building scenarios"],
  ["Arbitrary integrations", "Webhooks + API; no app marketplace", "The whole point — 3,000+ apps. Make wins this, clearly"],
];

export default function VsMake() {
  return (
    <SiteShell
      title="FrazzleCRM vs Make / Integromat (2026) — an honest comparison"
      description="FrazzleCRM vs Make (Integromat), honestly: Make is a brilliant general integration platform billed per operation. FrazzleCRM is a purpose-built funnel CRM — unlimited internal automation runs, flat pricing, and the map IS the automation."
    >

      <section className="bg-gradient-to-b from-indigo-50/60 to-white">
        <div className="mx-auto max-w-6xl px-6 pb-12 pt-16 text-center">
          <h1 className="mx-auto max-w-3xl text-[36px] font-extrabold leading-[1.15] tracking-tight text-slate-900 sm:text-[44px]">
            FrazzleCRM vs Make
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-[16px] leading-relaxed text-slate-600">
            Straight up: these are different categories. Make is a superb
            general-purpose integration platform — billed per operation.
            FrazzleCRM is a purpose-built funnel CRM where marketing
            automation runs are unlimited, because charging you per email
            step would be absurd.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-10">
        <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-sm">
          <table className="w-full min-w-[640px] border-collapse text-left text-[13.5px]">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/80 text-[12.5px] uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3 font-semibold"> </th>
                <th className="px-4 py-3 font-bold text-indigo-700">FrazzleCRM</th>
                <th className="px-4 py-3 font-semibold">Make</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map(([label, us, them]) => (
                <tr key={label} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-3.5 font-medium text-slate-700">{label}</td>
                  <td className="px-4 py-3.5 text-slate-700">{us}</td>
                  <td className="px-4 py-3.5 text-slate-500">{them}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-center text-[12px] text-slate-400">
          Make pricing as published July 2026. The honest answer for many
          teams: both — Frazzle for the funnel, Make for the long tail of
          integrations, joined by webhooks.
        </p>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-8">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
            <h2 className="text-[18px] font-bold text-slate-900">
              Choose Make if…
            </h2>
            <ul className="mt-3 space-y-2 text-[14px] leading-relaxed text-slate-600">
              <li>• You&apos;re connecting arbitrary apps — that&apos;s its superpower</li>
              <li>• Your workflows are operational, not marketing sequences</li>
              <li>• You&apos;re happy budgeting in operations/credits</li>
              <li>• You already have a CRM you love</li>
            </ul>
          </div>
          <div className="rounded-2xl border-2 border-indigo-200 bg-indigo-50/40 p-6 shadow-sm">
            <h2 className="text-[18px] font-bold text-slate-900">
              Choose FrazzleCRM if…
            </h2>
            <ul className="mt-3 space-y-2 text-[14px] leading-relaxed text-slate-700">
              <li>• The job is funnels: capture → nurture → sell → follow up</li>
              <li>• You want contacts, tags and appointments where the automation lives</li>
              <li>• Per-operation billing on a 5-email drip feels wrong to you</li>
              <li>• You want one map a client or teammate can read at a glance</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 pt-14">
        <FoundingMemberCallout note="No operations counter and no renewal — a five-email drip costs the same on day 1,000 as on day one." />
      </section>

      <section className="mx-auto max-w-6xl px-6 py-16 text-center">
        <h2 className="text-[26px] font-bold text-slate-900">
          Stop counting operations. Start drawing funnels.
        </h2>
        <p className="mx-auto mt-2 max-w-xl text-[14px] text-slate-600">
          Compare real costs with the{" "}
          <Link href="/tools/cost-calculator" className="font-medium text-indigo-600 hover:text-indigo-500">
            cost calculator
          </Link>
          , or generate a funnel free with the{" "}
          <Link href="/tools/funnel-generator" className="font-medium text-indigo-600 hover:text-indigo-500">
            funnel generator
          </Link>
          .
        </p>
        <a
          href={`${APP_URL}/signup`}
          className="mt-6 inline-block rounded-xl bg-indigo-600 px-7 py-3.5 text-[15px] font-semibold text-white shadow-lg shadow-indigo-200 hover:bg-indigo-500"
        >
          Start free
        </a>
      </section>
    </SiteShell>
  );
}
